/* Unified desktop/iPad Settings presentation. Native feature owners retain all durable state. */
(function initializeMemoraSettings(global) {
    'use strict';
    const d = global.document;
    const modal = d.getElementById('settings-modal');
    if (!modal) return;
    const coordinator = global.MemoraModalCoordinator;
    const sections = ['appearance', 'accessibility', 'ai', 'data', 'privacy', 'help'];
    const themeGroups = {
        core: [
            ['editorial-night', 'Default', '#28231d', '#d97757', '#e7a74f'],
            ['royal-obsidian', 'Obsidian', '#111014', '#cfa85e', '#f3e5ab'],
            ['nord-frost', 'Nord', '#202a31', '#88c0d0', '#b7d8df'],
            ['sakura', 'Sakura', '#2c1d25', '#ffb7c5', '#dcb2f4'],
            ['verdant-ink', 'Verdant', '#15231a', '#4fbf67', '#9fd39e']
        ],
        extra: [
            ['glassmorphism', 'Glass', '#20152f', '#8b5cf6', '#ec4899'],
            ['cozy-earth', 'Cozy', '#1f2923', '#9bb29c', '#d6b270'],
            ['ember-noir', 'Ember', '#281713', '#ff6b35', '#e7a74f'],
            ['carbon-night', 'Carbon', '#171b1a', '#10a37f', '#7b8b87']
        ]
    };
    const tips = [
        ['Start with Create', 'Add a PDF, PPTX, or quiz file to begin a study session.'],
        ['Keep studying while generation finishes', 'You can use other parts of the workspace while a quiz or Learn module is created.'],
        ['Use Sources when you need the original', 'Return to the source material beside your questions.'],
        ['Use Ask Memoraful for a gap', 'Ask about a question or a source when you need another explanation.'],
        ['Use Learn for different study modes', 'Choose Guided, Study Guide, Flashcards, or another available module.'],
        ['Save important questions', 'Bookmark questions you want to revisit.'],
        ['Use spaced review for retention', 'Return to questions when they are due.'],
        ['Use Stats to check progress', 'Review your study activity from the rail.'],
        ['Export when you need another workflow', 'Download the current quiz in a supported format.'],
        ['Database is the local storage manager', 'Review saved sessions and files from Data.']
    ];
    let activeSection = 'appearance';
    let opener = null;
    let openDropdown = null;
    const shortcutsPanel = d.getElementById('shortcuts-panel');
    const shortcutsHome = shortcutsPanel?.parentNode;
    const shortcutsNext = shortcutsPanel?.nextSibling;
    const shortcutRows = shortcutsPanel ? Array.from(shortcutsPanel.children).filter((node) => node.classList.contains('key-row')) : [];
    const databaseModal = d.getElementById('database-modal');
    const databaseHome = databaseModal?.parentNode;
    const databaseNext = databaseModal?.nextSibling;
    const apiKeyModal = d.getElementById('api-key-modal');
    const apiKeyHome = apiKeyModal?.parentNode;
    const apiKeyNext = apiKeyModal?.nextSibling;

    /* Settings > Data and Settings > AI hide their own rows while the Database or API key view is
       open inside them. Those were `:has(#…-modal.active)` rules, which made the browser re-check
       the whole page on every DOM insertion anywhere (slide text layers insert hundreds of nodes
       while they render). Each panel now carries an attribute kept in step with its modal's class. */
    const trackHostedSubview = (hostedModal, panelSelector, attributeName) => {
        if (!hostedModal || typeof MutationObserver !== 'function') return;
        const sync = () => {
            const hostPanel = hostedModal.closest(panelSelector);
            const hostedOpen = !!hostPanel && hostedModal.classList.contains('active');
            d.querySelectorAll(`${panelSelector}[${attributeName}]`).forEach((panel) => {
                if (!hostedOpen || panel !== hostPanel) panel.removeAttribute(attributeName);
            });
            if (hostedOpen) hostPanel.setAttribute(attributeName, '');
        };
        new MutationObserver(sync).observe(hostedModal, { attributes: true, attributeFilter: ['class'] });
    };
    trackHostedSubview(databaseModal, '[data-settings-panel="data"]', 'data-settings-database-open');
    trackHostedSubview(apiKeyModal, '[data-settings-panel="ai"]', 'data-settings-api-open');

    const byId = (id) => d.getElementById(id);
    const nativeClick = (id) => { const el = byId(id); if (el && !el.disabled) el.click(); };
    const isOpen = () => modal.classList.contains('active');
    let sliding = false;

    function reducedMotion() {
        return !!global.MemoraMotion?.isReduced?.();
    }

    // Child views (API key, Database) replace their section in place. Forward slides the section out
    // to the left and the child in from the right; Back (direction -1) runs the mirror image.
    /* Settings > Data > Recently deleted: the rail's deleted sessions and Learn content, listed in
       place like Database. The rail sessions module owns the list and the actions. */
    const deletedApi = () => global.MemoraLiveLeftRailSessions?.deleted || null;
    const escapeText = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

    async function renderDeletedView() {
        const list = modal.querySelector('[data-settings-deleted-list]');
        if (!list) return;
        let entries = [];
        try { entries = await deletedApi()?.list?.() || []; }
        catch (error) { console.warn('Recently deleted could not be read:', error); }
        if (!entries.length) {
            list.innerHTML = '<div class="settings-row"><div><strong>Nothing deleted recently</strong><small>Deleted sessions and Learn content appear here.</small></div></div>';
            return;
        }
        list.innerHTML = entries.map((entry) => {
            const days = deletedApi()?.daysLeft?.(entry);
            const left = days === 0 ? 'Removed today' : `${days} day${days === 1 ? '' : 's'} left`;
            const kind = entry.kind === 'learn' ? 'Learn content' : 'Session';
            const id = escapeText(entry.id);
            return `<div class="settings-row settings-deleted-row"><div><strong>${escapeText(entry.name || 'Untitled')}</strong><small>${kind} · ${left}</small></div><div class="settings-deleted-actions"><button type="button" class="settings-action" data-settings-deleted-restore="${id}">Restore</button><button type="button" class="settings-action is-danger" data-settings-deleted-purge="${id}">Delete forever</button></div></div>`;
        }).join('');
    }

    async function openDeletedView(panel) {
        const host = modal.querySelector('[data-settings-deleted-host]');
        if (!panel || !host) return;
        await slideSwap(panel, 1, async () => {
            panel.setAttribute('data-settings-deleted-open', '');
            host.hidden = false;
            modal.querySelector('.settings-main').scrollTop = 0;
            await renderDeletedView();
        });
        host.querySelector('[data-settings-deleted-back]')?.focus({ preventScroll: true });
    }

    function closeDeletedView() {
        modal.querySelector('[data-settings-panel="data"]')?.removeAttribute('data-settings-deleted-open');
        const host = modal.querySelector('[data-settings-deleted-host]');
        if (host) host.hidden = true;
    }

    async function runDeletedViewAction(button) {
        const restore = button.hasAttribute('data-settings-deleted-restore');
        const id = button.getAttribute(restore ? 'data-settings-deleted-restore' : 'data-settings-deleted-purge');
        modal.querySelectorAll('[data-settings-deleted-list] button').forEach((other) => { other.disabled = true; });
        try { await deletedApi()?.act?.(id, restore ? 'restore' : 'purge'); }
        catch (error) { console.warn('Recently deleted action failed:', error); }
        await renderDeletedView();
        // The clicked row is gone after the re-render; keep keyboard focus in this view.
        if (!modal.contains(global.document.activeElement)) {
            (modal.querySelector('[data-settings-deleted-list] button') || modal.querySelector('[data-settings-deleted-back]'))?.focus({ preventScroll: true });
        }
    }

    async function slideSwap(panel, direction, swap) {
        if (sliding) return;
        sliding = true;
        // A hidden tab pauses animations; never let a stalled one hold the view hostage.
        const settle = (animation, ms) => Promise.race([animation.finished.catch(() => {}), new Promise((done) => global.setTimeout(done, ms))]);
        try {
            if (!panel || reducedMotion() || typeof panel.animate !== 'function') { await swap(); return; }
            await settle(panel.animate([{ transform: 'none', opacity: 1 }, { transform: `translateX(${-28 * direction}px)`, opacity: 0 }],
                { duration: 140, easing: 'ease-in', fill: 'forwards' }), 240);
            try { await swap(); } finally {
                panel.getAnimations().forEach((animation) => animation.cancel());
                const enter = panel.animate([{ transform: `translateX(${36 * direction}px)`, opacity: 0 }, { transform: 'none', opacity: 1 }],
                    { duration: 260, easing: 'cubic-bezier(.22,.78,.2,1)' });
                await settle(enter, 380);
                enter.finish();
            }
        } finally {
            sliding = false;
        }
    }

    function renderThemeTiles() {
        for (const [group, options] of Object.entries(themeGroups)) {
            const host = modal.querySelector(`[data-settings-theme-group="${group}"]`);
            if (!host) continue;
            host.innerHTML = options.map(([id, label, a, b, c]) => `<button type="button" class="settings-theme-tile" data-settings-theme="${id}" aria-pressed="false"><span class="settings-theme-swatch" style="--sw-a:${a};--sw-b:${b};--sw-c:${c}"><i></i><b></b></span><span>${label}</span><span class="settings-theme-check" aria-hidden="true">&#10003;</span></button>`).join('');
        }
    }

    function syncThemes() {
        const current = d.documentElement.getAttribute('data-theme-overhaul') || 'editorial-night';
        modal.querySelectorAll('[data-settings-theme]').forEach((tile) => {
            const selected = tile.dataset.settingsTheme === current;
            tile.classList.toggle('is-active', selected);
            tile.setAttribute('aria-pressed', String(selected));
        });
        const style = global.getComputedStyle(modal);
        const utility = byId('settings-utility');
        const apiKey = byId('api-key-modal');
        for (const name of ['bg', 'bg2', 'surface', 'surface2', 'surface3', 'text', 'muted', 'accent', 'accent2']) {
            utility?.style.setProperty(`--settings-${name}`, style.getPropertyValue(`--settings-${name}`));
            apiKey?.style.setProperty(`--settings-${name}`, style.getPropertyValue(`--settings-${name}`));
        }
    }

    function syncFont() {
        const native = byId('font-select');
        const selected = native?.selectedOptions?.[0];
        const label = modal.querySelector('[data-settings-font-value]');
        if (label) label.textContent = selected?.value ? selected.textContent.replace(/\s*\([^)]*\)$/, '') : 'Inter';
    }

    function syncAccessibility() {
        const prefs = global.getAccessibilityPreferences?.() || {};
        for (const key of ['largeText', 'highContrast', 'reduceMotion']) {
            const button = modal.querySelector(`[data-settings-accessibility="${key}"]`);
            button?.setAttribute('aria-pressed', String(!!prefs[key]));
        }
    }

    function syncGameMode() {
        const allowed = global.isMemoraProductFeatureAllowed?.('game-mode') === true;
        const on = allowed && typeof isGameMode !== 'undefined' && !!isGameMode;
        const card = modal.querySelector('.settings-game-mode-card');
        if (card) card.hidden = !allowed;
        modal.querySelector('[data-settings-game-mode]')?.setAttribute('aria-pressed', String(on));
    }

    function syncNudges() {
        const on = global.MemoraReviewSchedule?.nudgesOn?.() !== false;
        modal.querySelector('[data-settings-nudges]')?.setAttribute('aria-pressed', String(on));
    }

    function syncAi() {
        const fast = byId('ai-fast-model-mode-toggle')?.getAttribute('aria-pressed') === 'true';
        modal.querySelector('[data-settings-fast]')?.setAttribute('aria-pressed', String(fast));
        modal.querySelector('[data-settings-ocr]')?.setAttribute('aria-pressed', String(global.getMemoraExtractionOcrEnabled?.() === true));
        const status = modal.querySelector('[data-settings-key-status]');
        if (status) {
            const connected = typeof global.hasMemoraPersonalGeminiApiKey === 'function'
                ? global.hasMemoraPersonalGeminiApiKey()
                : (typeof global.getSavedCustomGeminiAPIKey === 'function' && !!global.getSavedCustomGeminiAPIKey());
            status.textContent = connected ? 'Connected' : 'Not connected';
        }
        modal.querySelectorAll('select[data-memora-ai-setting]').forEach((select) => {
            const trigger = byId(`${select.id}-trigger`);
            if (!trigger) return;
            trigger.disabled = select.disabled;
            trigger.querySelector('span').textContent = select.selectedOptions?.[0]?.textContent || '';
        });
        const modelHelp = modal.querySelector('[data-memora-ask-model-setting-help]');
        if (modelHelp) modelHelp.textContent = 'Automatic keeps the current fallback behavior.';
    }

    function syncPrivacy() {
        const state = global.MemoraAnalytics?.getState?.() || { enabled: false, persisted: false };
        const toggle = modal.querySelector('[data-settings-analytics]');
        if (toggle) { toggle.setAttribute('aria-pressed', String(state.enabled)); toggle.disabled = !global.MemoraAnalytics || state.browserOptOut; }
        const status = modal.querySelector('[data-settings-analytics-status]');
        if (status) status.textContent = !state.persisted ? 'Off for this visit. Browser storage is unavailable.'
            : state.browserOptOut ? 'Off because of your browser’s privacy settings.'
                : state.enabled ? 'On in this browser. You can turn it off at any time.' : 'Off in this browser. New analytics events are blocked.';
    }

    function sync() { syncThemes(); syncFont(); syncAccessibility(); syncGameMode(); syncNudges(); syncAi(); syncPrivacy(); }

    function selectSection(section, focus = false) {
        closeDropdown();
        if (!sections.includes(section)) section = 'appearance';
        activeSection = section;
        modal.querySelectorAll('[data-settings-section]').forEach((tab) => {
            const selected = tab.dataset.settingsSection === section;
            tab.classList.toggle('is-active', selected);
            tab.toggleAttribute('aria-current', selected);
            if (selected && focus) tab.focus({ preventScroll: true });
        });
        modal.querySelectorAll('[data-settings-panel]').forEach((panel) => {
            panel.hidden = panel.dataset.settingsPanel !== section;
        });
        closeDeletedView();
        modal.querySelector('.settings-main').scrollTop = 0;
        if (section === 'data') {
            void global.describeMemoraStoragePersistence?.().then((text) => {
                const status = modal.querySelector('[data-settings-storage-persistence]');
                if (status && text) status.textContent = text;
            });
        }
        if (section === 'ai') {
            global.MemoraRuntime?.ensureFeature?.('ai').then(() => {
                if (isOpen() && activeSection === 'ai') {
                    moveAskForm();
                    global.syncMemoraAiExplanationSettingsForm?.(global.memoraAiExplanationSettings);
                    syncAi();
                    enhanceSelects();
                }
            }).catch((error) => console.warn('AI settings could not load:', error));
        }
        sync();
    }

    function open({ section = 'appearance', focus = null, opener: source = null } = {}) {
        closeDropdown();
        opener = source instanceof HTMLElement ? source : (d.activeElement instanceof HTMLElement ? d.activeElement : byId('menu-trigger-btn'));
        global.MemoraLiveLeftRailRouting?.admitNativeTransient?.('settings', opener);
        global.closeMenuDropdown?.();
        global.closeThemeModal?.();
        if (!isOpen()) global.MemoraAnalytics?.track?.('feature_used', { feature: section === 'privacy' ? 'privacy' : 'settings', action: 'open' });
        modal.classList.add('active');
        modal.setAttribute('aria-hidden', 'false');
        selectSection(section);
        coordinator?.open({ id: 'settings-modal', element: modal.querySelector('.settings-modal-card'), opener,
            isOpen, close: () => { close('coordinator'); return true; },
            initialFocus: focus === 'ask-memoraful' ? byId('memora-ai-setting-teaching-preset') : modal.querySelector(`[data-settings-section="${section}"]`) });
        return true;
    }

    function close(reason = 'button') {
        if (!isOpen()) return false;
        closeDropdown();
        closeUtility();
        if (apiKeyModal?.classList.contains('active')) global.closeAPIKeyModal?.();
        if (apiKeyModal?.parentNode !== apiKeyHome && apiKeyHome) {
            apiKeyHome.insertBefore(apiKeyModal, apiKeyNext?.parentNode === apiKeyHome ? apiKeyNext : null);
        }
        if (databaseModal?.classList.contains('active')) databaseModal.classList.remove('active');
        if (databaseModal?.parentNode !== databaseHome && databaseHome) {
            databaseHome.insertBefore(databaseModal, databaseNext?.parentNode === databaseHome ? databaseNext : null);
        }
        modal.classList.remove('active');
        modal.setAttribute('aria-hidden', 'true');
        if (reason !== 'coordinator') coordinator?.markClosed('settings-modal');
        if (reason !== 'coordinator' && opener?.isConnected) opener.focus({ preventScroll: true });
        opener = null;
        return true;
    }

    function openApiKey(options = {}) {
        const button = options.opener instanceof HTMLElement ? options.opener : d.activeElement;
        if (!isOpen()) open({ section: 'ai', opener: button });
        else if (activeSection !== 'ai') selectSection('ai');
        const host = modal.querySelector('[data-settings-api-host]');
        if (host && apiKeyModal) {
            host.appendChild(apiKeyModal);
            // The title and Back belong to the key card itself, not above it.
            const card = apiKeyModal.querySelector('.api-key-modal-content') || apiKeyModal;
            if (!card.querySelector(':scope > .settings-subview-head')) {
                apiKeyModal.querySelector('.settings-subview-head')?.remove();
                const head = d.createElement('div'); head.className = 'settings-subview-head';
                head.innerHTML = '<div><h3>Google Gemini API key</h3></div><button type="button" class="settings-action" data-settings-api-back>&larr; Back to AI</button>';
                // The key card stops click propagation, so Back cannot rely on the Settings listener.
                head.querySelector('[data-settings-api-back]').addEventListener('click', (backEvent) => {
                    void slideSwap(backEvent.currentTarget.closest('[data-settings-panel]'), -1, () => global.closeAPIKeyModal?.());
                });
                card.prepend(head);
            }
        }
        const showKey = async () => {
            await Promise.resolve(global.MemoraRuntime?.ensureFeature?.('ai'));
            if (!isOpen() || activeSection !== 'ai') return;
            global.MemoraLiveLeftRailRouting?.admitNativeTransient?.('settings', button);
            global.openAPIKeyModal?.({ ...options, opener: button, settingsHosted: true });
            modal.querySelector('.settings-main').scrollTop = 0;
        };
        return sliding || apiKeyModal?.classList.contains('active') ? showKey()
            : slideSwap(modal.querySelector('[data-settings-panel="ai"]'), 1, showKey);
    }

    function openUtility(kind) {
        const utility = byId('settings-utility');
        const title = byId('settings-utility-title');
        const subtitle = byId('settings-utility-subtitle');
        const body = byId('settings-utility-body');
        if (!utility || !title || !subtitle || !body) return;
        if (!utility.hidden) closeUtility();
        const headings = {
            tips: ['Tips', 'Practical shortcuts through the main Memoraful workflow.'],
            shortcuts: ['Keyboard shortcuts', 'Shortcuts currently exposed by the app.'],
            support: ['Support Memoraful', 'Donations help keep updates, polish, and bug fixes moving.'],
            export: ['Export quiz', 'Choose a format for the current quiz.'],
            usage: ['Local AI usage', 'Request estimates tracked in this browser today.']
        };
        [title.textContent, subtitle.textContent] = headings[kind] || headings.tips;
        body.replaceChildren();
        if (kind === 'tips') {
            const list = d.createElement('div');
            list.className = 'settings-tips-list';
            tips.forEach(([heading, copy]) => {
                const item = d.createElement('article');
                const h = d.createElement('h3'); h.textContent = heading;
                const p = d.createElement('p'); p.textContent = copy;
                item.append(h, p); list.appendChild(item);
            });
            body.appendChild(list);
        } else if (kind === 'shortcuts') {
            if (shortcutsPanel) {
                const groups = d.createElement('div'); groups.className = 'settings-shortcut-groups';
                for (const [heading, rowIndices] of [
                    ['Quiz', [0, 1, 2, 3, 4, 5, 6, 7, 8]],
                    ['Workspace', [14, 15, 16, 17, 18, 19, 20, 21]],
                    ['Tutor & voice', [9, 10, 11, 12, 13]],
                    ['Flashcards', [23, 24]]
                ]) {
                    const section = d.createElement('section'); section.className = 'settings-shortcut-group';
                    const h = d.createElement('h3'); h.textContent = heading; section.appendChild(h);
                    rowIndices.forEach((index) => { if (shortcutRows[index]) section.appendChild(shortcutRows[index]); });
                    groups.appendChild(section);
                }
                shortcutsPanel.appendChild(groups);
                if (shortcutRows[22]) shortcutRows[22].hidden = true;
                shortcutsPanel.style.display = 'block';
                body.appendChild(shortcutsPanel);
            }
        } else if (kind === 'export') {
            body.innerHTML = `<div class="settings-export-options">
                <button type="button" class="settings-export-choice" data-settings-export-format="anki"><strong>Anki (.txt)</strong><small>Tab-separated cards for Anki import</small></button>
                <button type="button" class="settings-export-choice" data-settings-export-format="quizlet"><strong>Quizlet (.txt)</strong><small>Copy-paste format for Quizlet</small></button>
                <button type="button" class="settings-export-choice" data-settings-export-format="csv"><strong>CSV spreadsheet</strong><small>Excel and Google Sheets compatible</small></button>
                <button type="button" class="settings-export-choice" data-settings-export-format="json"><strong>JSON backup</strong><small>Structured quiz data</small></button>
            </div><label class="settings-export-saved"><input type="checkbox" data-settings-export-saved><span>Export saved questions only</span></label><div class="settings-export-status" role="status" aria-live="polite"></div>`;
            const controlState = global.syncContentDependentControls?.();
            const checkbox = body.querySelector('[data-settings-export-saved]');
            checkbox.checked = !!byId('export-saved-only')?.checked;
            checkbox.disabled = !controlState?.hasSavedQuestions;
            if (!controlState?.hasCurrentQuestion && !controlState?.usableQuestionCount) {
                body.querySelector('.settings-export-status').textContent = 'No quiz questions are available yet.';
                body.querySelectorAll('[data-settings-export-format]').forEach((button) => { button.disabled = true; });
            }
        } else if (kind === 'usage') {
            const snapshot = global.getMemoraApiUsageSnapshot?.();
            const totals = snapshot?.totals || {};
            const cards = [
                ['Today', totals.total || 0],
                ['Successful', totals.success || 0],
                ['Failed', totals.failed || 0]
            ];
            const grid = d.createElement('div'); grid.className = 'settings-usage-grid';
            cards.forEach(([label, value]) => {
                const card = d.createElement('div'); card.className = 'settings-usage-card';
                const name = d.createElement('span'); name.textContent = label;
                const count = d.createElement('strong'); count.textContent = Number(value).toLocaleString();
                card.append(name, count); grid.appendChild(card);
            });
            body.appendChild(grid);
            const list = d.createElement('div'); list.className = 'settings-usage-list';
            const recent = snapshot?.recent?.slice(0, 12) || [];
            if (!recent.length) list.textContent = 'No requests tracked today.';
            recent.forEach((entry) => {
                const row = d.createElement('div'); row.className = 'settings-usage-line';
                const name = d.createElement('strong'); name.textContent = String(entry.surface || 'AI request');
                const time = d.createElement('span'); time.textContent = entry.at ? new Date(entry.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
                const outcome = d.createElement('span'); outcome.textContent = entry.ok ? 'OK' : String(entry.status || 'Failed');
                row.append(name, time, outcome); list.appendChild(row);
            });
            body.appendChild(list);
        } else {
            const intro = d.createElement('p');
            intro.className = 'settings-support-intro';
            intro.textContent = 'If Memoraful has made studying easier or saved you time, you can support continued development through one of the existing options.';
            body.appendChild(intro);
            const methods = d.querySelector('.welcome-donate .welcome-donate-methods');
            if (methods) {
                const list = d.createElement('div'); list.className = 'settings-support-methods';
                methods.querySelectorAll('a[href]').forEach((native) => {
                    const link = d.createElement('a'); link.className = 'settings-support-method';
                    link.href = native.href; link.target = native.target; link.rel = native.rel;
                    const copy = d.createElement('div');
                    const heading = d.createElement('strong'); heading.textContent = native.querySelector('.welcome-donate-label')?.textContent || 'Support';
                    const detail = d.createElement('small'); detail.textContent = heading.textContent === 'Contact Us!' ? 'Send a message' : `Open ${heading.textContent}`;
                    copy.append(heading, detail);
                    const action = d.createElement('span'); action.textContent = 'Open ↗';
                    link.append(copy, action); list.appendChild(link);
                });
                body.appendChild(list);
            }
        }
        utility.hidden = false;
        coordinator?.open({ id: 'settings-utility', element: utility.querySelector('.settings-utility-card'), opener: d.activeElement,
            parentLayerId: 'settings-modal', isOpen: () => !utility.hidden,
            close: () => { closeUtility(); return true; } });
    }

    function closeUtility() {
        const utility = byId('settings-utility');
        if (!utility || utility.hidden) return;
        utility.hidden = true;
        if (shortcutsPanel?.parentNode === byId('settings-utility-body') && shortcutsHome) {
            const groups = shortcutsPanel.querySelector(':scope > .settings-shortcut-groups');
            const hint = byId('shortcut-edit-hint');
            shortcutRows.forEach((row) => { row.hidden = false; shortcutsPanel.insertBefore(row, hint?.parentNode === shortcutsPanel ? hint : groups); });
            groups?.remove();
            shortcutsPanel.style.display = 'none';
            shortcutsHome.insertBefore(shortcutsPanel, shortcutsNext?.parentNode === shortcutsHome ? shortcutsNext : null);
            coordinator?.markClosed('shortcuts-panel');
        }
        coordinator?.markClosed('settings-utility');
    }

    function registerChild(id, closeChild, openerButton) {
        const child = byId(id);
        if (!child?.classList.contains('active')) return;
        coordinator?.open({ id, element: child.querySelector('[role="dialog"]') || child, opener: openerButton,
            parentLayerId: 'settings-modal', isOpen: () => child.classList.contains('active'),
            close: () => { closeChild(); return true; } });
    }

    let syncedDatabaseRender = null;

    function formatStorageBytes(bytes) {
        if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`;
        if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
        return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    }

    function storedRecordBytes(value) {
        if (!value) return 0;
        if (typeof value.size === 'number') return value.size;
        if (typeof value.byteLength === 'number') return value.byteLength;
        try { return new Blob([typeof value === 'string' ? value : JSON.stringify(value)]).size; } catch (_) { return 0; }
    }

    // A session's footprint is its record plus the PDFs and quiz banks it lists. Files are stored
    // once by name, so a file two sessions share counts toward both. Reads run one at a time:
    // a PDF saved as an ArrayBuffer is materialized whole by the read.
    async function sizeDatabaseSessions(rows) {
        if (typeof IDB === 'undefined' || !IDB?.get) return;
        const cache = new Map();
        const measure = async (store, key) => {
            const cacheKey = `${store}\n${key}`;
            if (!cache.has(cacheKey)) {
                let bytes = 0;
                try { bytes = storedRecordBytes(await IDB.get(store, key)); } catch (_) { /* Missing records count as zero. */ }
                cache.set(cacheKey, bytes);
            }
            return cache.get(cacheKey);
        };
        for (const row of rows) {
            if (!row.meta.isConnected) continue;
            const id = row.session.querySelector('[data-db-action="delete-session"]')?.dataset.sessionId || '';
            let bytes = id ? await measure('sessions', id) : 0;
            for (const button of row.session.querySelectorAll('[data-db-action="download-file"]')) {
                const type = button.dataset.dbFileType;
                const store = type === 'pdf' ? 'files' : type === 'json' ? 'questions' : '';
                if (store && button.dataset.dbFilename) bytes += await measure(store, button.dataset.dbFilename);
            }
            if (!row.meta.isConnected) continue;
            row.meta.textContent = `${row.countText} · ${formatStorageBytes(bytes)} · ${row.detailText}`;
            row.meta.title = 'Size counts this session’s saved PDFs, quiz banks, and progress. A file shared with another session counts toward each.';
        }
    }

    async function syncDatabaseSummary() {
        const list = byId('db-content-list');
        const content = databaseModal?.querySelector('.database-modal-content');
        if (!list || !content) return;
        // Each render replaces the list's children, so its first child marks one render.
        if (list.firstElementChild && list.firstElementChild === syncedDatabaseRender) return;
        syncedDatabaseRender = list.firstElementChild;
        if (!list.dataset.settingsRenderObserved) {
            // Deleting a session re-renders the list natively; restyle that render too.
            list.dataset.settingsRenderObserved = '1';
            new MutationObserver(() => {
                if (isOpen() && databaseModal?.classList.contains('active')) void syncDatabaseSummary();
            }).observe(list, { childList: true });
        }
        let summary = content.querySelector('.settings-db-summary');
        if (!summary) {
            summary = d.createElement('div');
            summary.className = 'settings-db-summary';
            content.insertBefore(summary, list);
        }
        const sessions = list.querySelectorAll('.db-session-card');
        let pdfs = 0; let quizzes = 0;
        const sized = [];
        sessions.forEach((session) => {
            const counts = { pdfs: 0, quizzes: 0, guides: 0 };
            session.querySelectorAll('.db-col').forEach((column) => {
                const header = column.querySelector('.db-col-header');
                if (!header) return;
                const count = Number(header.textContent.match(/\((\d+)\)/)?.[1] || 0);
                if (/lecture|pdf/i.test(header.textContent) && !/explanation/i.test(header.textContent)) {
                    counts.pdfs = count; pdfs += count; header.textContent = `PDFs (${count})`;
                } else if (/quiz/i.test(header.textContent)) {
                    counts.quizzes = count; quizzes += count; header.textContent = `Quiz banks (${count})`;
                } else if (/explanation/i.test(header.textContent)) {
                    header.textContent = `Saved answer PDFs (${count})`;
                } else if (/guide/i.test(header.textContent)) {
                    counts.guides = count; header.textContent = `Study guides (${count})`;
                }
                column.hidden = count === 0;
            });
            const name = session.querySelector('.db-session-name');
            if (name) name.title = name.textContent;
            const meta = session.querySelector('.db-session-meta');
            if (meta) {
                const countText = `${counts.pdfs} PDF${counts.pdfs === 1 ? '' : 's'} · ${counts.quizzes} quiz bank${counts.quizzes === 1 ? '' : 's'}${counts.guides ? ` · ${counts.guides} guide${counts.guides === 1 ? '' : 's'}` : ''}`;
                const detailText = meta.textContent;
                meta.textContent = `${countText} · ${detailText}`;
                sized.push({ session, meta, countText, detailText });
            }
            const header = session.querySelector('.db-session-header');
            if (header) { header.tabIndex = 0; header.setAttribute('role', 'button'); header.setAttribute('aria-expanded', 'false'); }
            session.classList.add('collapsed');
            const content = session.querySelector('.db-session-content');
            const actions = session.querySelector('.db-session-actions');
            if (content && actions) {
                const toolbar = d.createElement('div'); toolbar.className = 'settings-db-toolbar';
                actions.querySelectorAll('button').forEach((button) => {
                    if (button.dataset.dbAction === 'export-duplicates' && button.getAttribute('aria-disabled') === 'true') return;
                    if (button.dataset.dbAction === 'export-duplicates') button.querySelector('.db-session-action-label').textContent = 'Download duplicates';
                    if (button.dataset.dbAction === 'download-session') button.querySelector('.db-session-action-label').textContent = 'Download files';
                    // Delete stays in the row, beside Open, so a session can go without expanding it.
                    if (button.dataset.dbAction === 'delete-session') {
                        button.textContent = 'Delete';
                        button.setAttribute('aria-label', `Delete session ${name?.textContent || ''}`.trim());
                        return;
                    }
                    toolbar.appendChild(button);
                });
                content.prepend(toolbar);
                if (!session.querySelector('.db-col:not([hidden])')) {
                    const empty = d.createElement('p'); empty.className = 'settings-db-empty'; empty.textContent = 'No files saved in this session.';
                    content.appendChild(empty);
                }
                content.querySelectorAll('[data-db-action="download-file"]').forEach((button) => {
                    const filename = button.dataset.dbFilename || 'file';
                    button.textContent = 'Download';
                    button.setAttribute('aria-label', `Download ${filename}`);
                });
            }
        });
        if (!list.dataset.settingsKeyboardBound) {
            list.dataset.settingsKeyboardBound = '1';
            list.addEventListener('keydown', (event) => {
                if (!event.target.classList?.contains('db-session-header') || !['Enter', ' '].includes(event.key)) return;
                event.preventDefault();
                global.toggleDbSection?.(event.target.dataset.dbSectionId || '');
                event.target.setAttribute('aria-expanded', String(!event.target.closest('.db-session-card')?.classList.contains('collapsed')));
            });
            list.addEventListener('click', (event) => {
                const header = event.target.closest('.db-session-header');
                if (header) global.setTimeout(() => header.setAttribute('aria-expanded', String(!header.closest('.db-session-card')?.classList.contains('collapsed'))), 0);
            });
        }
        let storage = '—';
        try {
            const bytes = (await global.navigator.storage?.estimate?.())?.usage;
            if (Number.isFinite(bytes)) storage = formatStorageBytes(bytes);
        } catch (_) { /* Storage estimates are optional. */ }
        void sizeDatabaseSessions(sized);
        summary.replaceChildren();
        for (const [label, value] of [['Sessions', sessions.length], ['PDF files', pdfs], ['Quiz banks', quizzes], ['Local storage', storage]]) {
            const card = d.createElement('div'); card.className = 'settings-db-stat';
            const title = d.createElement('span'); title.textContent = label;
            const count = d.createElement('strong'); count.textContent = String(value);
            card.append(title, count); summary.appendChild(card);
        }
    }

    function closeDropdown() {
        if (!openDropdown) return;
        openDropdown.menu.remove();
        openDropdown.trigger.setAttribute('aria-expanded', 'false');
        openDropdown = null;
        coordinator?.markClosed('settings-dropdown');
    }

    function showDropdown(trigger, select) {
        closeDropdown();
        const menu = d.createElement('div');
        menu.className = 'settings-dropdown-menu';
        menu.setAttribute('role', 'listbox');
        menu.id = `${trigger.id}-menu`;
        const modalStyle = global.getComputedStyle(modal);
        for (const name of ['surface2', 'surface3', 'text', 'muted', 'accent']) {
            menu.style.setProperty(`--settings-${name}`, modalStyle.getPropertyValue(`--settings-${name}`));
        }
        menu.style.setProperty('--settings-dropdown-bg', modalStyle.getPropertyValue('--settings-surface2'));
        Array.from(select.options).filter((option) => option.value).forEach((option) => {
            const item = d.createElement('button');
            item.type = 'button'; item.className = 'settings-dropdown-option';
            item.setAttribute('role', 'option');
            item.setAttribute('aria-selected', String(select.value === option.value));
            item.textContent = select.id === 'font-select' ? option.textContent.replace(/\s*\([^)]*\)$/, '') : option.textContent;
            item.addEventListener('click', () => {
                select.value = option.value;
                select.dispatchEvent(new Event('change', { bubbles: true }));
                if (select.id === 'font-select') syncFont();
                else trigger.querySelector('span').textContent = option.textContent;
                closeDropdown();
                trigger.focus({ preventScroll: true });
            });
            menu.appendChild(item);
        });
        d.body.appendChild(menu);
        const rect = trigger.getBoundingClientRect();
        const shell = modal.querySelector('.settings-modal-card').getBoundingClientRect();
        const topBoundary = Math.max(8, shell.top + 8);
        const bottomBoundary = Math.min(global.innerHeight - 8, shell.bottom - 8);
        const desiredHeight = Math.min(menu.scrollHeight, 310);
        const belowSpace = Math.max(0, bottomBoundary - rect.bottom - 6);
        const aboveSpace = Math.max(0, rect.top - topBoundary - 6);
        const above = belowSpace < desiredHeight && aboveSpace > belowSpace;
        const height = Math.max(70, Math.min(desiredHeight, above ? aboveSpace : belowSpace));
        menu.style.width = `${Math.max(rect.width, 210)}px`;
        menu.style.left = `${Math.max(8, Math.min(rect.left, global.innerWidth - menu.offsetWidth - 8))}px`;
        menu.style.maxHeight = `${height}px`;
        menu.style.top = `${above ? Math.max(8, rect.top - height - 6) : rect.bottom + 6}px`;
        trigger.setAttribute('aria-expanded', 'true');
        openDropdown = { trigger, menu };
        coordinator?.open({ id: 'settings-dropdown', element: menu, opener: trigger,
            parentLayerId: 'settings-modal', isOpen: () => !!openDropdown,
            close: () => { closeDropdown(); return true; } });
        (menu.querySelector('[aria-selected="true"]') || menu.firstElementChild)?.focus({ preventScroll: true });
    }

    function enhanceSelects() {
        modal.querySelectorAll('select[data-memora-ai-setting]').forEach((select) => {
            if (select.dataset.settingsEnhanced) return;
            select.dataset.settingsEnhanced = '1';
            select.classList.add('settings-native-select');
            const trigger = d.createElement('button');
            trigger.type = 'button'; trigger.className = 'settings-dropdown-trigger';
            trigger.id = `${select.id}-trigger`;
            trigger.setAttribute('aria-haspopup', 'listbox');
            trigger.setAttribute('aria-expanded', 'false');
            trigger.setAttribute('aria-label', `${select.closest('label')?.querySelector('span')?.textContent || 'Setting'}: choose option`);
            trigger.innerHTML = '<span></span><b aria-hidden="true">&#9662;</b>';
            select.after(trigger);
            const update = () => { trigger.querySelector('span').textContent = select.selectedOptions?.[0]?.textContent || ''; trigger.disabled = select.disabled; };
            select.addEventListener('change', update);
            trigger.addEventListener('click', (event) => { event.preventDefault(); showDropdown(trigger, select); });
            update();
        });
    }

    function moveAskForm() {
        const slot = byId('settings-ask-form-slot');
        const form = byId('memora-ai-settings-form');
        if (slot && form && form.parentElement !== slot) { slot.appendChild(form); enhanceSelects(); }
    }

    renderThemeTiles();
    moveAskForm();
    const fontSelect = byId('font-select');
    const fontTrigger = byId('settings-font-trigger');
    fontTrigger?.addEventListener('click', () => showDropdown(fontTrigger, fontSelect));
    modal.addEventListener('click', (event) => {
        if (event.target === modal || event.target.closest('[data-settings-close]')) { close(); return; }
        const tab = event.target.closest('[data-settings-section]');
        if (tab) { selectSection(tab.dataset.settingsSection); return; }
        const tile = event.target.closest('[data-settings-theme]');
        if (tile) { global.setThemeOverhaul?.(tile.dataset.settingsTheme); syncThemes(); return; }
        const more = event.target.closest('[data-settings-more]');
        if (more) {
            const extras = modal.querySelector('[data-settings-theme-group="extra"]');
            extras.hidden = !extras.hidden;
            more.textContent = extras.hidden ? 'More themes' : 'Hide themes';
            more.setAttribute('aria-expanded', String(!extras.hidden));
            return;
        }
        if (event.target.closest('[data-settings-reset]')) {
            global.setThemeOverhaul?.('editorial-night');
            if (fontSelect) { fontSelect.value = ''; fontSelect.dispatchEvent(new Event('change', { bubbles: true })); }
            sync(); return;
        }
        if (event.target.closest('[data-settings-analytics]')) {
            global.MemoraAnalytics?.setEnabled?.(!global.MemoraAnalytics.getState().enabled); syncPrivacy(); return;
        }
        const a11y = event.target.closest('[data-settings-accessibility]');
        if (a11y) { global.toggleAccessibilityPreference?.(a11y.dataset.settingsAccessibility); syncAccessibility(); return; }
        if (event.target.closest('[data-settings-game-mode]')) { global.toggleGameMode?.(); syncGameMode(); return; }
        if (event.target.closest('[data-settings-nudges]')) {
            global.MemoraReviewSchedule?.setNudges?.(global.MemoraReviewSchedule?.nudgesOn?.() === false);
            syncNudges();
            return;
        }
        if (event.target.closest('[data-settings-fast]')) { nativeClick('ai-fast-model-mode-toggle'); syncAi(); return; }
        if (event.target.closest('[data-settings-ocr]')) { global.setMemoraExtractionOcrEnabled?.(global.getMemoraExtractionOcrEnabled?.() !== true); syncAi(); return; }
        if (event.target.closest('[data-settings-api-key]')) {
            void openApiKey({ opener: event.target.closest('button') });
            return;
        }
        if (event.target.closest('[data-settings-usage]')) {
            openUtility('usage');
            return;
        }
        if (event.target.closest('[data-settings-database]')) {
            const button = event.target.closest('button');
            const host = modal.querySelector('[data-settings-database-host]');
            if (host && databaseModal) {
                host.appendChild(databaseModal);
                if (!databaseModal.querySelector('.settings-subview-head')) {
                    const head = d.createElement('div');
                    head.className = 'settings-subview-head';
                    head.innerHTML = '<div><h3>Database</h3><p>Review information saved in this browser.</p></div><button type="button" class="settings-action" data-settings-database-back>&larr; Back to Data</button>';
                    databaseModal.prepend(head);
                }
            }
            void slideSwap(button.closest('[data-settings-panel]'), 1, async () => {
                await Promise.resolve(global.openDatabaseModal?.());
                if (!isOpen()) return;
                modal.querySelector('.settings-main').scrollTop = 0;
                await syncDatabaseSummary();
                registerChild('database-modal', () => databaseModal?.classList.remove('active'), button);
            });
            return;
        }
        if (event.target.closest('[data-settings-database-back]')) {
            void slideSwap(event.target.closest('[data-settings-panel]'), -1, () => {
                databaseModal?.classList.remove('active');
                coordinator?.markClosed('database-modal');
            });
            return;
        }
        if (event.target.closest('[data-settings-deleted]')) {
            void openDeletedView(event.target.closest('[data-settings-panel]'));
            return;
        }
        if (event.target.closest('[data-settings-deleted-back]')) {
            const panel = event.target.closest('[data-settings-panel]');
            void slideSwap(panel, -1, () => closeDeletedView());
            return;
        }
        const deletedAction = event.target.closest('[data-settings-deleted-restore], [data-settings-deleted-purge]');
        if (deletedAction) {
            void runDeletedViewAction(deletedAction);
            return;
        }
        if (event.target.closest('[data-settings-export]')) {
            openUtility('export');
            return;
        }
        if (event.target.closest('[data-settings-shortcuts]')) { event.stopPropagation(); openUtility('shortcuts'); return; }
        const utility = event.target.closest('[data-settings-utility]');
        if (utility) openUtility(utility.dataset.settingsUtility);
    });
    byId('settings-utility')?.addEventListener('click', (event) => {
        const format = event.target.closest('[data-settings-export-format]');
        if (format) {
            const nativeCheckbox = byId('export-saved-only');
            if (nativeCheckbox) nativeCheckbox.checked = !!byId('settings-utility-body')?.querySelector('[data-settings-export-saved]')?.checked;
            const saved = global.exportQuiz?.(format.dataset.settingsExportFormat);
            const status = byId('settings-utility-body')?.querySelector('.settings-export-status');
            if (saved && status) status.textContent = 'Download started.';
            return;
        }
        if (event.target.id === 'settings-utility' || event.target.closest('[data-settings-utility-close]')) {
            coordinator?.close('settings-utility', 'button');
        }
    });
    d.addEventListener('pointerdown', (event) => {
        if (openDropdown && !openDropdown.menu.contains(event.target) && !openDropdown.trigger.contains(event.target)) closeDropdown();
    }, true);
    d.addEventListener('keydown', (event) => {
        if (!openDropdown) return;
        if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); const trigger = openDropdown.trigger; closeDropdown(); trigger.focus(); return; }
        const options = Array.from(openDropdown.menu.querySelectorAll('[role="option"]'));
        const index = options.indexOf(d.activeElement);
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault(); event.stopImmediatePropagation();
            options[(index + (event.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length]?.focus();
        }
    }, true);
    byId('menu-trigger-btn')?.addEventListener('click', (event) => {
        event.preventDefault(); event.stopImmediatePropagation(); open({ opener: event.currentTarget });
    }, true);
    const legacyThemeButton = byId('theme-menu-btn');
    if (legacyThemeButton) legacyThemeButton.onclick = () => open({ section: 'appearance', opener: legacyThemeButton });
    global.addEventListener('memora:theme-overhaul-changed', syncThemes);
    global.addEventListener('memora:accessibility-preferences-changed', syncAccessibility);
    global.addEventListener('memora:game-mode-changed', syncGameMode);
    const gameModeButton = byId('game-mode-toggle');
    if (gameModeButton) new MutationObserver(syncGameMode).observe(gameModeButton, { attributes: true, attributeFilter: ['class'] });
    global.addEventListener('memora:analytics-preference-changed', syncPrivacy);
    global.addEventListener('memora:api-key-saved', syncAi);
    global.addEventListener('memora:api-key-removed', syncAi);
    global.addEventListener('memora:api-key-saved', syncGameMode);
    global.addEventListener('memora:api-key-removed', syncGameMode);
    global.MemoraSettings = { open, close, selectSection, openApiKey, openChild: openUtility, sync };
    global.openSettings = (section = 'appearance', options = {}) => open({ section, ...options });
    global.closeSettings = () => close();
})(window);
